import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CloseButton, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAt,
  faInfoCircle,
  faLayerGroup,
  faToggleOff
} from '@fortawesome/free-solid-svg-icons';
import IconAlert from 'components/common/IconAlert';
import useRequestState from 'hooks/useRequestState';
import {
  NETWORK_ERROR_MESSAGE,
  getApiErrorMessage,
  removeAllowedDomain
} from 'helpers/registrationApi';
import { ToneIcon } from 'components/registration-shared/RegistrationUI';

/**
 * Confirm before switching a domain to Inactive: from then on, people who
 * register with that domain are asked for their agency's name instead of
 * being linked to the group automatically. The domain stays on the list and
 * can be switched back on.
 */
const DeactivateDomainModal = ({
  show,
  row,
  groupName,
  onHide,
  onDeactivated
}) => {
  const deactivateReq = useRequestState();
  const [error, setError] = useState('');
  const deactivating = deactivateReq.isLoading;
  const rowId = row?.id;

  useEffect(() => {
    if (show) {
      setError('');
    }
  }, [show, rowId]);

  const handleDeactivate = async () => {
    if (!row || deactivating) {
      return;
    }
    setError('');
    const result = await deactivateReq.run(() => removeAllowedDomain(row.id));
    if (result.stale) {
      return;
    }
    if (!result.ok) {
      const message = getApiErrorMessage(result.error, NETWORK_ERROR_MESSAGE);
      setError(message);
      toast.error(message);
      return;
    }
    if (result.data.ok) {
      onDeactivated(row);
      return;
    }
    const message = result.data.message || 'Unable to deactivate this domain.';
    setError(message);
    toast.error(message);
  };

  const claims = row?.claim_count || 0;

  return (
    <Modal
      show={show}
      onHide={deactivating ? undefined : onHide}
      className="mym-queue-modal"
      backdrop={deactivating ? 'static' : true}
      keyboard={!deactivating}
      centered
      aria-labelledby="deactivate-domain-title"
    >
      <div className="mym-modal-head mym-tone-danger">
        <ToneIcon icon={faToggleOff} tone="danger" size="lg" solid />
        <div className="min-w-0">
          <h5 id="deactivate-domain-title" className="mym-modal-title">
            Deactivate email domain?
          </h5>
          <p className="mym-modal-sub">You can turn it back on later.</p>
        </div>
        <CloseButton
          onClick={onHide}
          disabled={deactivating}
          aria-label="Close"
        />
      </div>

      <Modal.Body className="p-3 p-md-4">
        {row && (
          <>
            <div className="mym-applicant-summary mb-3">
              <ToneIcon icon={faAt} tone="primary" />
              <div className="flex-1 min-w-0">
                <div className="mym-domain-name text-truncate">
                  {row.domain}
                </div>
                <div className="fs-10 text-600 text-truncate">
                  <FontAwesomeIcon icon={faLayerGroup} className="me-1" />
                  {groupName}
                </div>
              </div>
            </div>
            <p className="fs-10 text-700 mb-3">
              New requests from <strong>name@{row.domain}</strong> emails will
              no longer go to <strong>{groupName}</strong> automatically.
              Applicants will be asked for their agency's name instead.
            </p>
            {claims > 0 && (
              <div className="mym-inline-note mym-tone-info">
                <FontAwesomeIcon icon={faInfoCircle} className="mt-1" />
                <div>
                  {claims === 1
                    ? '1 sign-up has used this domain so far.'
                    : `${claims} sign-ups have used this domain so far.`}
                </div>
              </div>
            )}
            {error && (
              <IconAlert variant="danger" className="mt-3 mb-0">
                <p className="mb-0 fw-semibold">{error}</p>
              </IconAlert>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="falcon-default"
          onClick={onHide}
          disabled={deactivating}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleDeactivate}
          disabled={deactivating}
        >
          {deactivating ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Deactivating…
            </>
          ) : (
            'Deactivate'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

DeactivateDomainModal.propTypes = {
  show: PropTypes.bool.isRequired,
  row: PropTypes.object,
  groupName: PropTypes.string,
  onHide: PropTypes.func.isRequired,
  onDeactivated: PropTypes.func.isRequired
};

export default DeactivateDomainModal;
